/* eslint-disable @angular-eslint/component-class-suffix */

import {
  Component,
  forwardRef,
  Input,
  OnInit,
  TemplateRef
} from '@angular/core';

import {
  AbstractControl,
  ControlValueAccessor,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';

import * as Utils from '@fry/ui-material/utils';

//
// Type guards
//

function isValidValue(value: unknown): value is FUIValueListItem[] {
  if(!(value instanceof Array)) { return false; }
  if(value.length === 0) { return true; }
  return 'key' in value[0] && 'value' in value[0];
}

/**
 * We expect just a KEY & VALUE interface from the items we're rendering in
 * the list.
 */
export interface FUIValueListItem {
  key: string;
  value: string|number;
}

// What consumers can expect as return value from the control
export type FUIValueListControlValue = FUIValueListItem[];

@Component({
    selector: 'fui-value-list-control',
    templateUrl: './value-list.control.html',
    styleUrls: ['./value-list.control.css'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FUIValueListControl),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => FUIValueListControl),
            multi: true
        }
    ],
    standalone: false
})
export class FUIValueListControl implements OnInit, ControlValueAccessor, Validator {

  @Input()
  public captureKeys: boolean = true;

  @Input()
  public title?: string;

  @Input()
  public keyFieldTitle?: string;

  @Input()
  public valueFieldTitle?: string;

  @Input()
  public isEmptyTemplate?: TemplateRef<unknown>;

  public form!: UntypedFormGroup;

  public get formItemsArray(): UntypedFormArray {
    return this.form.get('items') as UntypedFormArray;
  }

  public get formItems(): UntypedFormGroup[] {
    return this.formItemsArray.controls as UntypedFormGroup[];
  }

  constructor(private formBuilder: UntypedFormBuilder) {}

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      items: this.formBuilder.array([])
    });
  }

  private createItemGroup(item?: FUIValueListItem): UntypedFormGroup {
    const controls: {[K in keyof FUIValueListItem]: unknown} = {
      // eslint-disable-next-line @typescript-eslint/unbound-method
      key: [item ? item.key : '', Validators.required],
      // eslint-disable-next-line @typescript-eslint/unbound-method
      value: [item ? item.value : '', Validators.required]
    };
    return this.formBuilder.group(controls);
  }

  //
  // Validator
  //

  validate(_control: AbstractControl): ValidationErrors | null {
    const errors: ValidationErrors = {
      invalidMetadataForm: {
        valid: false,
        message: $localize `Metadata fields are invalid`
      }
    };
    // tslint:disable-next-line: no-null-keyword
    return this.form.valid ? null : errors;
  }

  //
  // ControlValueAccessor
  //

  public onTouched: () => void = () => { return; };

  writeValue(value: unknown): void {
    if(!isValidValue(value)) { return; }

    value.map(item => this.createItemGroup(item))
         .forEach(group => this.formItemsArray.push(group));
  }

  registerOnChange(callback: (_: unknown) => void): void {
    // As FormArray must be contained within a FormGrup we need to unwrap
    // the value from 'items' to pass correct value upstream.
    this.form.valueChanges.subscribe((value: {items: FUIValueListItem[]}) => {
      callback(value.items);
    });
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.form.disable() : this.form.enable();
  }

  //
  // Actions
  //

  public onAdd(): void {
    const item = this.captureKeys
                 ? undefined
                 : { key: Utils.randomString(), value: '' };
    this.formItemsArray.push(this.createItemGroup(item));
  }

  public onDelete(index: number): void {
    this.formItemsArray.removeAt(index);
  }

  //
  // Template helpers
  //

  public get isEmpty(): boolean {
    return this.formItemsArray.length === 0;
  }

  public getControlFor(index: number, key: string): AbstractControl|undefined {
    return this.formItemsArray.get(`${index}.${key}`) ?? undefined;
  }

  public getTitle(): string {
    return this.title ?? $localize `Key value list control`;
  }

  public getKeyLabel(): string {
    return this.keyFieldTitle ?? $localize `Key`;
  }

  public getValueLabel(): string {
    return this.valueFieldTitle ?? $localize `Value`;
  }

  public getDeleteButtonLabel(item: AbstractControl): string {
    return $localize `Delete ${item.get('key')} - ${item.get('value')}`;
  }
}
